/**
 * career-login — the signed-out surface (never a silent redirect loop). Offers
 * the real Keycloak login (a full-page GET to the BFF) and, in dev mode, a
 * Dev-Anmeldung button that establishes a synthetic session compatible with the
 * api's stub-auth. `returnTo` is honored on both paths.
 */

import { KitElement, type ActionMap, type KitContext, type ReadonlySignal, type ViewModel } from "@platform/web-kit"
import { html, nothing, type TemplateResult } from "@platform/web-kit/render"
import { I18nToken, SessionToken, apiPost } from "../services"
import type { CareerI18n } from "../../i18n"
import type { ShellSession } from "../domain"

class LoginVm implements ViewModel {
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>> = []
  readonly t: CareerI18n["t"]
  constructor(i18n: CareerI18n, readonly session: ShellSession) {
    this.t = i18n.t
  }

  get returnTo(): string {
    return new URL(globalThis.location.href).searchParams.get("returnTo") ?? "/search"
  }

  async devLogin(): Promise<void> {
    await apiPost("/api/auth/dev-login", { returnTo: this.returnTo })
    // Full reload so the shell re-fetches the (now signed-in) session.
    globalThis.location.assign(this.returnTo)
  }
}

export class CareerLogin extends KitElement<LoginVm> {
  protected createViewModel(ctx: KitContext): LoginVm {
    return new LoginVm(ctx.inject(I18nToken), ctx.inject(SessionToken))
  }

  protected render(vm: LoginVm): TemplateResult {
    const loginHref = `/api/auth/login?returnTo=${encodeURIComponent(vm.returnTo)}`
    return html`
      <main class="login" aria-labelledby="career-login-title">
        <span class="login-hatch login-hatch-tl" aria-hidden="true"></span>
        <span class="login-hatch login-hatch-br" aria-hidden="true"></span>
        <section class="login-panel">
          <div class="login-brand">
            <!-- The full platform lockup — »Career« in the P's gap (§brand: the in-gap
                 name is reserved for LARGE surfaces; the shell uses the bare P.). The
                 mark is decorative here — the visually-hidden <h1> carries the name. -->
            <kit-brand-mark app=${vm.t("app.name")} space="personal" animated aria-hidden="true"></kit-brand-mark>
            <h1 id="career-login-title" class="visually-hidden">${vm.t("brand.name")}</h1>
          </div>
          <span class="login-rule" aria-hidden="true"></span>
          <p class="login-body" lang="de">${vm.t("login.body")}</p>
          <a class="login-cta link-reset" href=${loginHref} lang="de">
            <span>${vm.t("login.cta")}</span>
            <svg class="login-cta-arrow" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M5 12h14" /><path d="m13 6 6 6-6 6" />
            </svg>
          </a>
          ${vm.session.devMode
            ? html`
                <button class="login-dev" data-act="dev-login" lang="de">${vm.t("auth.devLogin")}</button>
                <p class="login-hint" lang="de">${vm.t("login.devHint")}</p>
              `
            : nothing}
        </section>
      </main>
    `
  }

  protected override actions(vm: LoginVm): ActionMap {
    return { "dev-login": () => void vm.devLogin() }
  }
}
